import { toRaw, unref, type MaybeRefOrGetter } from "vue";
import { isArray, isObject } from "../assertate";
import { isNil, compact } from "../lodash";
import type { InputSingleValue, PromiseOrFunction, I18nTranslate, MaybeTransform, MaybeGetter } from "../types";
import { tryParseJson, trySerializeJson } from "./serialization";
import { cloneMagicKey } from "./uniqueID";

/*
 * Create a deep copy of a JS object
 */
export function vueStructuredClone<T>(value: MaybeRefOrGetter<T>, options?: StructuredSerializeOptions) {
  const val = unref(value);
  const result = structuredClone(toRaw(val), options) as T;
  cloneMagicKey(val, result);
  return result;
}

/*
 * Currency
 */
export const createCurrencyFormatter = (locale: string) => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
  });
};

export const formatAsCurrency = (value = 0, locale = "en") => {
  const formatter = createCurrencyFormatter(locale);
  return formatter.format(value);
};

/*
 * Debounce - a timeout
 */
const createDebounce = () => {
  let timeout: ReturnType<typeof setTimeout>;
  return function (fnc: () => void, delayMs = 500) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fnc();
    }, delayMs || 500);
  };
};

export const debounce = createDebounce();

/*
 * Return static default arrays and objects
 */
export function getCertificationLengthList(t: I18nTranslate) {
  return [
    {
      label: t("formFields.years1"),
      value: 365,
    },
    {
      label: t("formFields.years2"),
      value: 730,
    },
    {
      label: t("formFields.years3"),
      value: 1095,
    },
    {
      label: t("formFields.lifetimeCertification"),
      value: 9999,
    },
  ];
}

/*
 * Format certification length label for display
 */
export function formatCertificationLength(val: number | undefined | null, t: I18nTranslate) {
  if (!val) return ""; // TODO (JM): test
  const opts = getCertificationLengthList(t);
  const currentOpt = opts.find((opt) => opt.value === val);
  if (currentOpt) return currentOpt.label;

  return `${val} ${t("formFields.days")}`;
}

/**
 * A generic interface for anything that has a skill string
 */
export interface HasSkillsString {
  skill: string;
}

/**
 * Return all skills as a comma separated string
 * @param listWithSkills - A list of items that have a `skills` property
 */
export function getAllSkills<T extends HasSkillsString = HasSkillsString>(listWithSkills: T[]) {
  if (!isArray(listWithSkills)) return "";
  const skills = listWithSkills?.reduce((acc: string[], row: T) => {
    let formattedSkill: string | null = null;
    if (row?.skill && row?.skill?.trim()?.length > 0) {
      formattedSkill = row.skill.trim();
    }

    if (formattedSkill === null) return acc;

    const found = acc.find((r) => r === formattedSkill);
    if (!found) {
      acc.push(formattedSkill);
    }
    return acc;
  }, []);
  return skills.join(", ");
}

/*
 * For form controls -
 * If no prop text, return fallback text
 */
export function checkPropText(propText: string | undefined, fallback: string): string {
  if (!propText || propText.length === 0) return fallback;
  return propText;
}

/*
 * Utils that return what to display based on element state
 * getListClass - CSS for zebra striped lists
 */
export function getListClass(active: boolean, selected: boolean, index: number, fallback: string) {
  if (active || selected) return "bg-nblue-3 z-50";
  if (index % 2 === 0) return "bg-nblue-4";
  return fallback;
}

/*
 * Get the default item in an array
 */
export function getDefaultNumber(array?: { default: boolean; number: number }[] | null) {
  if (!array || !isArray(array)) return null;
  const defaultItem = array.find((item) => item.default === true);
  if (defaultItem === undefined) return null;
  return defaultItem.number;
}

/**
 * An empty function to be used by calls we do not need to await.
 */
export const DO_NOTHING = () => {};
export const PASS_THROUGH = <T>(v: T) => v;

export function createWaitForPromise(
  testFn: () => boolean,
  timeout: number = 5,
  maxRetries: number = 10,
  rejectAfterMaxTries?: boolean,
): Promise<boolean> {
  let currentTry = 0;
  return new Promise<boolean>((res, rej) => {
    const loopMethod = () => {
      const result = testFn();
      if (result) {
        return res(result);
      }
      currentTry++;
      if (currentTry > maxRetries) {
        return rejectAfterMaxTries ? rej(false) : res(false);
      }
      setTimeout(loopMethod, timeout);
    };
    loopMethod();
  });
}

// TODO: similar functionality implemented on src/ckClient/composables/zodForm.ts [removeClientKeysTransform]
/**
 * Removes $ prefixed fields from the request payload.
 * This is useful when sending data to the backend.
 *
 * @param payload
 * @returns same payload with no '$' prefixed properties
 */
export const sanitizeRequestPayload = <I = unknown, O extends I = I>(
  payload: I,
  { deep }: { deep: boolean } = { deep: true },
): O => {
  if (isArray(payload)) {
    if (!deep) return payload as O;
    return payload.map((item) => sanitizeRequestPayload(item, { deep })) as O;
  }

  if (isObject(payload) && !(payload instanceof Blob || payload instanceof File)) {
    return Object.entries(payload).reduce(
      (acc, [key, value]) => (key.startsWith("$") ? acc : { ...acc, [key]: sanitizeRequestPayload(value, { deep }) }),
      {},
    ) as O;
  }

  return payload as O;
};

/**
 * This exception will eventually be shown to the user.
 * It is used to wrap errors that are user-friendly and can be translated.
 */
export class UserFriendlyError extends Error {
  translatable: true;

  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.translatable = true;
    this.name = "UserFriendlyError";
  }
}

export class PageFriendlyError extends Error {
  constructor(
    public data: {
      title?: string;
      heading?: string;
      subHeading?: string;
      message?: string;
      actionText?: MaybeGetter<string>;
      redirect?: MaybeGetter<string>;
    },
    errorOptions?: ErrorOptions,
  ) {
    super(data?.heading ?? "", errorOptions);
    this.message = data?.message ?? "";
    this.name = "PageFriendlyError";
  }
}

export function unwrapGetter<T>(fnOrValue: MaybeGetter<T>): T {
  return fnOrValue instanceof Function ? fnOrValue() : fnOrValue;
}

export function unwrapPromise<T>(promise: PromiseOrFunction<T>): Promise<T> {
  return unwrapGetter(promise);
}

export function resolveTransform<T_Data, T_Result = T_Data>(
  value: T_Data | undefined,
  maybeValue: MaybeTransform<T_Data | undefined, T_Result>,
): T_Result | T_Data {
  return maybeValue instanceof Function ? maybeValue(value) : (value ?? maybeValue);
}

export function formatPX(value: number | undefined) {
  if (isNil(value)) return;
  return `${value ?? 0}px`;
}

export function makeKey(values: InputSingleValue[]) {
  return compact(values).join("-");
}

export function obfuscateParams(params: Record<string, string | number | undefined>) {
  return btoa(trySerializeJson(params));
}

export function parseObfuscatedParams<P extends Record<string, string | number | undefined>>(
  encryptedParams: string | undefined,
): P | undefined {
  if (!encryptedParams) return;
  return tryParseJson<P | undefined>(atob(encryptedParams), undefined);
}
